import React, { useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { notify } from '@/components/ui/notify';
import StatusSymbol from '@/components/procurement/StatusSymbol';
import { DEAL_STATUS_PICKER_OPTIONS, getDealStatusDisplay } from '@/lib/api/myTenders';

const OPTIONS = DEAL_STATUS_PICKER_OPTIONS.map((o) => ({
  value: o.value,
  label: o.value === 'untouched' ? 'Untouched' : o.label,
}));

/**
 * The My Deals status, changeable in place. Same vocabulary and symbols as Live Radar / My Deals
 * (StatusSymbol + getDealStatusDisplay); writes through the same /my-tenders/status endpoint.
 */
export default function DealStatusMenu({ dealId, status, onChange, saving = false, triggerLabel }) {
  const [open, setOpen] = useState(false);
  const current = status || null;
  const display = getDealStatusDisplay(current);

  const pick = async (value) => {
    setOpen(false);
    if ((value === 'untouched' && !current) || value === current) return;
    try {
      await onChange(dealId, value);
    } catch (e) {
      notify({ title: 'Could not update the status', description: e?.message || 'Please try again', variant: 'destructive' });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={saving}
          aria-label={`Change status, now ${display.label}`}
          className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-[#0A1A2F] hover:bg-gray-50 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <StatusSymbol status={current} size={14} />}
          {triggerLabel || display.label}
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1">
        <div role="menu" aria-label="Deal status" className="flex flex-col">
          {OPTIONS.map((o) => {
            const isCurrent = (o.value === 'untouched' && !current) || o.value === current;
            return (
              <button
                key={o.value}
                type="button"
                role="menuitem"
                onClick={() => pick(o.value)}
                className={`flex h-8 items-center gap-2 rounded px-2 text-left text-xs font-medium text-[#0A1A2F] hover:bg-gray-100 ${isCurrent ? 'bg-gray-100' : ''}`}
              >
                <StatusSymbol status={o.value === 'untouched' ? null : o.value} size={14} />
                {o.label}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
